import {
    CabinClass,
    FerryAddon,
    FerryLeg,
    FerrySegment,
    FlightLeg,
    FlightSegment,
} from '@/api/Cart/cart.interfaces';
import { isString } from 'lodash';
import {
    FerryAddon as FerryAddonMyBooking,
    FerryLeg as FerryLegMyBooking,
    FerrySegment as FerrySegmentBooking,
    FlightLeg as FlightLegMyBooking,
    FlightSegment as FlightSegmentMyBooking,
} from '@/api/MyBooking/myBooking.interfaces';
import {
    Journey,
    JourneyCompany,
    JourneyExtra,
    JourneyExtraType,
    JourneyExtraVehicle,
    JourneySegment,
} from '@ui/components/Journey/Journey.interfaces';
import { convertToDate, getDateAvoidingZeroTimezone } from './datetime/datetime';
import { t } from './translation';
import { getFullTravelTime, getTravelTime } from '@ui/components/Journey/Journey.helpers';
import { configBase } from '@/config/base';

export enum MapDataType {
    Checkout = 'Checkout',
    MyBooking = 'MyBooking',
}

const SHOW_CLASS_FOR_FLIGHTS = false;

export function mapFlightJourney(
    leg: FlightLeg | FlightLegMyBooking,
    type: MapDataType
): Journey {
    switch (type) {
        case MapDataType.MyBooking:
            return mapBookingFlightJourney(leg as FlightLegMyBooking);
        default:
            return mapCheckoutFlightJourney(leg as FlightLeg);
    }
}

const getCarrierLogo = (carrier?: string | null): string | null => {
    const useStaticUrl = configBase.devMode.enabled;

    const baseUrl = useStaticUrl ? 'https://www.sembo.se' : window.location.origin;

    return carrier ? `${baseUrl}/d/Images/flight/airlines/${carrier}.gif` : null;
};

const getStopTime = (time: string | null | undefined) => {
    if (isString(time) && time === '-') {
        return '';
    }

    return time;
};

function mapBookingFlightJourney(leg: FlightLegMyBooking): Journey {
    const cabinClass = (c: string) =>
        SHOW_CLASS_FOR_FLIGHTS && c ? t({ key: `shared.cabin_${c.toLowerCase()}` }) : '';

    const getLabels = (s: FlightSegmentMyBooking): string[] => {
        const items: string[] = [];

        const showOperatedBy =
            s.operatingCarrierName && s.operatingCarrierName !== s.marketingCarrierName;

        if (showOperatedBy) {
            items.push(
                t({
                    key: 'shared.operated_by',
                    values: [s.operatingCarrierName],
                    prefix: '(',
                    suffix: ')',
                })
            );
        }

        return items;
    };

    return {
        type: 'Flight',
        time: leg.travelTime,
        company: {
            number: null,
            name: leg.platingCarrierName,
            logo: leg.platingCarrierLogo,
        },
        segments: leg.segments.map((s: FlightSegmentMyBooking) => ({
            time: s.flightTime,
            stopTime: getStopTime(s.stopOverTime),
            class: cabinClass(s.cabinClassType),
            company: {
                number: s.flightNumber,
                name: s.marketingCarrierName,
                logo: s.marketingCarrierLogo ?? getCarrierLogo(s.marketingCarrier),
            },
            departure: {
                date: s.departureDate ? convertToDate(s.departureDate) : null,
                name: s.departureAirportName,
                location: null,
                code: null, // Name already has "iata" code from BE
            },
            arrival: {
                date: s.arrivalDate ? convertToDate(s.arrivalDate) : null,
                name: s.arrivalAirportName,
                location: null,
                code: null, // Name already has "iata" code from BE
            },
            labels: getLabels(s),
            technicalStop: s.hasTechnicalStop,
        })),
    };
}

function mapCheckoutFlightJourney(leg: FlightLeg): Journey {
    const cabinClass = (c: number) =>
        SHOW_CLASS_FOR_FLIGHTS && c
            ? t({ key: `shared.cabin_${CabinClass[c].toLowerCase()}` })
            : '';

    const getLabels = (s: FlightSegment): string[] => {
        const items: string[] = [];

        const showOperatedBy =
            s.OperatingCarrierName && s.OperatingCarrierName !== s.MarketingCarrier;

        if (showOperatedBy) {
            items.push(
                t({
                    key: 'shared.operated_by',
                    values: [s.OperatingCarrierName],
                    prefix: '(',
                    suffix: ')',
                })
            );
        }

        return items;
    };

    return {
        type: 'Flight',
        time: leg.TravelTime,
        company: {
            number: null,
            name: leg.PlatingCarrierName,
            logo: leg.PlatingCarrierLogo,
        },
        segments: leg.Segments.map((s: FlightSegment) => ({
            time: s.FlightTime,
            stopTime: s.StopOverTime,
            technicalStop: s.HasTechnicalStop,
            class: cabinClass(s.CabinClass),
            company: {
                number: s.FlightNumber,
                name: s.MarketingCarrierName,
                logo: s.CompanyLogo ?? getCarrierLogo(s.MarketingCarrier),
            },
            departure: {
                date: convertToDate(s.DepartureDate),
                name: s.DepartureAirportName,
                location: s.DepartureLocationName,
                code: null, // Name already has "iata" code from BE
            },
            arrival: {
                date: convertToDate(s.ArrivalDate),
                name: s.ArrivalAirportName,
                location: s.ArrivalLocationName,
                code: null, // Name already has "iata" code from BE
            },
            labels: getLabels(s),
        })),
    };
}

export function mapFerryJourney(
    leg: FerryLeg | FerryLegMyBooking,
    type: MapDataType
): Journey {
    switch (type) {
        case MapDataType.MyBooking:
            return mapBookingFerryJourney(leg as FerryLegMyBooking);
        default: {
            return mapCheckoutFerryJourney(leg as FerryLeg);
        }
    }
}

function mapCheckoutFerryJourney(leg: FerryLeg): Journey {
    const segments: JourneySegment[] = leg.Segments.map((s: FerrySegment) => {
        const departureDate = getDateAvoidingZeroTimezone(s.DepartureDate);
        const arrivalDate = getDateAvoidingZeroTimezone(s.ArrivalDate);
        const time = getTravelTime(departureDate, arrivalDate);

        const company: JourneyCompany = {
            number: null,
            logo: s?.CompanyLogo,
            name: s?.Company,
            ferryName: s?.FerryName,
        };

        const mapVehicles = (): JourneyExtraVehicle[] => {
            const items: JourneyExtraVehicle[] = [];

            (s.Vehicles ?? []).forEach((x) => {
                if (x.Type) {
                    items.push({
                        type: JourneyExtraType.Vehicle,
                        title: x.Description,
                        description: x.Description ?? undefined,
                        quantity: 1,
                        vehicleType: x.Type,
                    });
                }
            });

            return items;
        };

        const mapAddons = (items: FerryAddon[], type: JourneyExtraType): JourneyExtra[] => {
            return (items || []).map((x) => ({
                type,
                title: x.Title,
                quantity: x.Quantity,
                price: x.Price,
            }));
        };

        const extras: JourneyExtra[] = [
            ...mapAddons(s.Meals, JourneyExtraType.Meal),
            ...mapAddons(s.Accommodations, JourneyExtraType.Accommodation),
            ...mapAddons(s.Lounges, JourneyExtraType.Lounge),
            ...mapAddons(s.Pets, JourneyExtraType.Pet),
            ...mapVehicles(),
        ];

        return {
            time: time,
            stopTime: null,
            class: s.TicketType?.Name,
            company,
            departure: {
                date: departureDate,
                name: s.DeparturePortName ?? null,
                location: s.DepartureLocationName ?? null,
                code: s.DeparturePortCode ?? null,
            },
            arrival: {
                date: arrivalDate,
                name: s.ArrivalPortName ?? null,
                location: s.ArrivalLocationName ?? null,
                code: s.ArrivalPortCode ?? null,
            },
            labels: null,
            extras,
        };
    });

    return {
        type: 'Ferry',
        time: getFullTravelTime(segments),
        company: segments[0].company,
        segments,
    };
}

function mapBookingFerryJourney(leg: FerryLegMyBooking): Journey {
    const mapAddons = (
        items: FerryAddonMyBooking[],
        type: JourneyExtraType
    ): JourneyExtra[] => {
        return (items || []).map((x) => ({
            type,
            title: x.title,
            quantity: x.quantity,
        }));
    };

    // const mapVehicles = (): JourneyExtra[] => {
    //     const items: JourneyExtra[] = [];

    //     (leg.vehicles ?? []).forEach((x) => {
    //         if (x.LicensePlate) {
    //             items.push({
    //                 type: JourneyExtraType.Vehicle,
    //                 title: x.Description,
    //                 description: x.LicensePlate ?? undefined,
    //                 quantity: 1,
    //             });
    //         }
    //     });

    //     return items;
    // };

    const segments: JourneySegment[] = leg.segments.map((s: FerrySegmentBooking) => {
        const departureDate = getDateAvoidingZeroTimezone(s.departureDate);
        const arrivalDate = getDateAvoidingZeroTimezone(s.arrivalDate);
        const time = getTravelTime(departureDate, arrivalDate);

        const extras: JourneyExtra[] = [
            ...mapAddons(s.meals, JourneyExtraType.Meal),
            ...mapAddons(s.accommodations, JourneyExtraType.Accommodation),
            ...mapAddons(s.lounges, JourneyExtraType.Lounge),
            ...mapAddons(s.pets, JourneyExtraType.Pet),
            // ...mapVehicles(),
        ];

        const company: JourneyCompany = {
            number: null,
            name: null,
            ferryName: s?.ferryName,
            logo: s?.operatingCarrierLogoUrl,
        };

        return {
            company,
            time: time,
            stopTime: null,
            class: null,
            departure: {
                date: departureDate,
                name: s?.departurePortName ?? null,
                location: s?.departurePortLocationName ?? null,
                code: s?.departurePortCode ?? null,
            },
            arrival: {
                date: arrivalDate,
                name: s?.arrivalPortName ?? null,
                location: s?.arrivalPortLocationName ?? null,
                code: s?.arrivalPortCode ?? null,
            },
            labels: null,
            extras,
        };
    });

    return {
        type: 'Ferry',
        time: getFullTravelTime(segments),
        company: segments[0].company,
        segments,
    };
}
